'use client';
import { useEffect, useRef, useState, useContext } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { Address } from 'shared/types/account';
import { TokenizeCardResults, TokenizedCardResponse } from 'shared/types/creditCard/CreditCard';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { SoldAccount } from 'shared/types/soa/customer/SoaCustomer';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import InputField from '@/components/customercenter-ui/common/validateForm/inputField';
import {
  CART_STATE_FROZEN,
  DEFAULT_COUNTRY,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
  DATE_FORMAT_MONTH_DATE_YYYY,
  ISO_DATE_TIME_FORMAT,
} from '@/components/customercenter-ui/components/customer-detail/constants';
import {
  transformCustomerDetailsData,
  transformShippingAddress,
} from '@/components/customercenter-ui/components/customer-detail/utils';
import EditAddressModal from '@/components/customercenter-ui/components/modals/customerDetailAction/editAddress';
import OrderTotalMismatchModal from '@/components/customercenter-ui/components/modals/customerResult/orderTotalMistmatch';
import { CustomFloatingSelect } from '@/components/customercenter-ui/components/ui/customSelect/floatingSelect';
import { useToast } from '@/components/customercenter-ui/components/ui/use-toast';
import StepsDesktop from '@/components/customercenter-ui/icons/stepsDesktop';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import { Button } from '@/components/customercenter-ui/ui/button';
import { showSoldAccountSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { maskPhoneNumber, maskPostalCode, convertDateFormat } from '@/components/customercenter-ui/utils/formatUtils';
import { PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount, useCart, useCustomObjects } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { GlobalPageTexts } from '@/helpers/i18n';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification } from '@/helpers/utils/notification';
import { PaymentHelpers } from '@/helpers/utils/paymentHelpers';
import { getAgentName, getCookieValue, TranslateText } from '@/helpers/utils/siteUtils';
import {
  CustomerAddressPayload,
  PhoneRecordingPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import CreditInformation from './CreditInformation';
import NewCreditCard from './NewCreditCard';
import PaymentTitle from './PaymentTitle';
import {
  ORDER_ACCEPTABLE,
  cardRegex,
  COOKIE_KEYS,
  DEFAULT_SITE_LANG,
  MAX_PAGE_SIZE,
} from '../../../../../constants/constant';
import { createPaymentSchema } from '../schemas';
export interface PaymentProps {
  tasticData: any;
}

export const soaCardTypes: Record<string, string> = {
  visa: 'Visa',
  americanexpress: 'AmericanExpress',
  mastercard: 'Mastercard',
  amex: 'AmericanExpress',
  discover: 'Discover',
};

export function getCardType(cardNumber: string, setCardType: any) {
  const cleanedCardNumber = cardNumber.replace(/\D/g, '');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cardType = Object.entries(cardRegex).find(([_, regex]) => regex.test(cleanedCardNumber))?.[0];
  setCardType(cardType ?? 'unknown');
}

export const handleCardNumberChange = (event: any, setCardNumber: any) => {
  const inputValue = event.target.value?.replace(/\D/g, '');
  const chunks = [0, 4, 8, 12];
  const maskedValue = chunks
    .reduce((acc, curr, i) => {
      const chunk = inputValue.slice(curr, chunks[i + 1]);
      return acc.concat(chunk, i < chunks.length - 1 ? ' ' : '');
    }, '')
    .trim();

  setCardNumber(maskedValue);
};

const Payment: React.FC<PaymentProps> = ({ tasticData }) => {
  const {
    shippingAddress,
    goToCheckoutStep,
    setTokenizeData,
    setCardDetails,
    selectedPaymentMethod,
    billingAddress,
    setBillingAddressValue,
    newAddress,
    setCreditDetails,
    setCashDetails,
    setCheckDetails,
    setNotificationMsg,
    setError,
    setContactInformation,
    setEncryptedCCDetails,
    contactInformation,
    setValidatePaymentBatchResult,
    setFormValues,
    soaCustomer,
    setSoaCustomer,
    isNoChargeOrder,
    isMailOrder,
    setCustomerDetailsResponse,
    customerDetailsResponse: customerAccountResponse,
    customerAccountAddresses,
    setCustomerAccountAddresses,
    contextCart,
    setContextCart,
  } = useCheckout() as any;
  const params = useSearchParams();
  const [openEditAddressModal, setOpenEditAddressModal] = useState(false);
  const [mismatchModalOpen, setMismatchModalOpen] = useState(false);
  const { setCustomerData, setCustomerDetailsResponse: setCreditSummaryDetailsResponse } =
    useContext(CreditSummaryContext);
  const cartData = contextCart;
  const [proceedFurther, setProceedFurther] = useState(false);
  const [soldAccountData, setSoldAccountData] = useState<SoldAccount>();
  const { getDebtBuyerData } = useCustomObjects();
  const projectSettings: ProjectSettings = useProjSettings();
  const { tokenizeCard, getCustomerAddresses, getCustomerDetails, startPhoneRecording, stopPhoneRecording } =
    useAccount();
  const { updateCart, validatePaymentBatch } = useCart(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const [customerAddresses, setCustomerAddresses] = useState<any>([]);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<any>(null);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [debtBuyerCode, setDebtBuyerCode] = useState<string>('');
  const [accountSoldOn, setAccountSoldOn] = useState<string>('');
  const customerId = params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '';
  const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? 'ZB';
  const defaultValues = billingAddress ? { ...billingAddress } : {};
  const creditCardType = 'newCreditCard';
  const PaymentSchema = createPaymentSchema({ isMailOrder: isMailOrder });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    unregister,
    clearErrors,
    control,
    getValues,
  } = useForm({
    resolver: zodResolver(PaymentSchema),
    defaultValues: defaultValues,
    mode: 'all',
  });

  const unregisterExceptFields = (keepFieldNames: string[]) => {
    const allFields = Object.keys(control._fields);
    const fieldsToUnregister = allFields.filter((fieldName) => !keepFieldNames.includes(fieldName));

    fieldsToUnregister.forEach((fieldName) => {
      unregister(fieldName);
    });
  };

  const getLocalAddressesData = async () => {
    setIsLoading(true);
    try {
      let responseBillingAddress = null;
      let customerDetailsResponse = null;
      const customerId = params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '';
      if (
        customerAccountResponse?.code === RESPONSE_SUCCESS_MESSAGE &&
        customerAccountResponse?.Customer?.customerId === customerId
      ) {
        customerDetailsResponse = customerAccountResponse;
        if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          setSoaCustomer(customerDetailsResponse?.Customer);
          responseBillingAddress = mapCustomerDataToBillingAddress(customerDetailsResponse);
          setSelectedBillingAddress(responseBillingAddress);
          const { primaryEmail }: any = transformCustomerDetailsData({
            customerDetails: customerDetailsResponse,
          } as any);
          if (primaryEmail) {
            setContactInformation({ email: primaryEmail });
          } else if (cartData?.email) {
            setContactInformation({ email: cartData.email });
          }
          const isSoldAccount =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ??
            false;
          setIsSoldAccount(isSoldAccount);
          const debtBuyerCode =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.debtBuyerCode;
          setDebtBuyerCode(debtBuyerCode ?? '');
          const accountSoldOn =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.accountSoldOn;
          if (accountSoldOn) {
            setAccountSoldOn(convertDateFormat(accountSoldOn, ISO_DATE_TIME_FORMAT, DATE_FORMAT_MONTH_DATE_YYYY));
          }
          setCustomerData(customerDetailsResponse?.Customer);
          setCreditSummaryDetailsResponse(customerDetailsResponse);
        }
      } else {
        customerDetailsResponse = await getCustomerDetails({
          query: {
            customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '',
            omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID) ?? '',
            divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
            durationInMonths: params.get(PAYLOAD_QUERY.durationInMonths) ?? '',
            creditAccountId: '',
            mailingRecordId: '',
            idName: 'customerId',
          },
        });
        if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          setSoaCustomer(customerDetailsResponse?.Customer);
          responseBillingAddress = mapCustomerDataToBillingAddress(customerDetailsResponse);
          setSelectedBillingAddress(responseBillingAddress);
          const { primaryEmail }: any = transformCustomerDetailsData({
            customerDetails: customerDetailsResponse,
          } as any);
          if (primaryEmail) {
            setContactInformation({ email: primaryEmail });
          } else if (cartData?.email) {
            setContactInformation({ email: cartData.email });
          }
          const isSoldAccount =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ??
            false;
          setIsSoldAccount(isSoldAccount);
          const debtBuyerCode =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.debtBuyerCode;
          setDebtBuyerCode(debtBuyerCode ?? '');
          const accountSoldOn =
            customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.accountSoldOn;
          if (accountSoldOn) {
            setAccountSoldOn(convertDateFormat(accountSoldOn, ISO_DATE_TIME_FORMAT, DATE_FORMAT_MONTH_DATE_YYYY));
          }
          setCustomerDetailsResponse(customerDetailsResponse);
          setCustomerData(customerDetailsResponse?.Customer);
          setCreditSummaryDetailsResponse(customerDetailsResponse);
        }
      }
      let addresses: any[] = [];
      if (customerAccountAddresses && customerAccountAddresses?.length > 0) {
        if (responseBillingAddress) {
          addresses = [responseBillingAddress, ...addresses];
        }
        addresses = [...addresses, ...customerAccountAddresses];
        setCustomerAddresses(addresses);
      } else {
        const payload = {
          query: {
            omsCustomerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '',
            pageSize: MAX_PAGE_SIZE,
            divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
          },
        } as CustomerAddressPayload;
        const customerAddressesResponse = await getCustomerAddresses(payload);
        if (customerAddressesResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          const transformedAddress = transformShippingAddress(customerAddressesResponse?.RecipientAddresses || []);
          setCustomerAccountAddresses(transformedAddress);
          addresses = [...transformedAddress];
          if (responseBillingAddress) {
            addresses = [responseBillingAddress, ...addresses];
          }
          addresses = [...addresses, ...transformedAddress];
          setCustomerAddresses(addresses);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const run = async () => {
      await getLocalAddressesData();
      await stopRecording();
    };
    run();
  }, []);

  function mapCustomerDataToBillingAddress(customerData: any) {
    const customerInfo = customerData.Customer.CustomerInfo;
    const billingAddress = customerData.Customer.BillingAddress.LocationInfo;
    const primaryMobileNumber = customerData?.Customer?.DivisionCustomers?.[0]?.PhoneRecords?.filter(
      (phoneData: any) => phoneData?.HeaderInfo?.isPrimary,
    );
    return {
      fullName: `${customerInfo.firstName} ${customerInfo.lastName}`,
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      streetName: billingAddress.line1,
      city: billingAddress.city,
      country: billingAddress.country,
      state: billingAddress.state,
      postalCode: billingAddress.zip,
      additionalAddressInfo: billingAddress.line2 || '',
      phone: primaryMobileNumber?.[0]?.PhoneInfo?.phoneNumber ?? '',
      addressId: 'B-ADD',
    };
  }

  useEffect(() => {
    reset();
    if (selectedPaymentMethod == PaymentMethods.CREDIT) {
      unregisterExceptFields(['dob', 'ssn', 'catalogId']);
      clearErrors();
    }
    if (selectedPaymentMethod == PaymentMethods.CARD) {
      unregisterExceptFields(['cardNumber', 'expDate', 'securityCode', 'email']);
      clearErrors();
    }
    if (selectedPaymentMethod == PaymentMethods.CASH) {
      unregisterExceptFields(['cashAmount', 'batchNumber']);
      clearErrors();
    }

    if (selectedPaymentMethod == PaymentMethods.CHECK) {
      unregisterExceptFields(['batchNumber', 'checkNumber', 'totalCheckAmount']);
      clearErrors();
    }
  }, [selectedPaymentMethod]);

  const ValidateDecimal = (event: { target: { value: any } }, beforeDecimal = 9, afterDecimal = 2) => {
    let { value } = event.target;
    value = value
      .replace(/[^\d.]/g, '')
      .replace(new RegExp(`(^\\d{${beforeDecimal}})\\d`, 'g'), '$1')
      .replace(/(\..*)\./g, '$1')
      .replace(new RegExp(`(\\.\\d{${afterDecimal}}).`, 'g'), '$1');

    event.target.value = value;
  };

  const handleInputChange = (event: { target: any }) => {
    ValidateDecimal(event, 9, 2);
  };

  const getPaymentComponent = () => {
    switch (selectedPaymentMethod) {
      case PaymentMethods.CARD:
        return (
          <section className="border-b border-foggyGray px-4 pt-12 lg:px-26">
            {creditCardType === 'newCreditCard' && (
              <section>
                <NewCreditCard register={register} errors={errors} reset={reset} />
              </section>
            )}
          </section>
        );
      case PaymentMethods.CREDIT:
        return (
          <CreditInformation
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
            data={tasticData}
          />
        );
      case PaymentMethods.CASH:
        return (
          <section className="test border-b border-foggyGray px-4 pb-10 pt-12 lg:w-3/4 lg:px-26">
            <InputField
              classname="mt-8 w-full p-3 "
              label="batchNumber"
              items={mapLocaleToMeaningfulFormat().batchNumberPlaceholder}
              id={'dob'}
              htmlFor={'dob'}
              type={'text'}
              register={register}
              errors={errors}
              maxlength={20}
            />
            <InputField
              classname="mt-8 w-full p-3"
              label="cashAmount"
              items={mapLocaleToMeaningfulFormat().cashNumberPlaceholder}
              type={'number'}
              id={'cashAmount'}
              htmlFor={'cashAmount'}
              register={register}
              errors={errors}
              onChange={handleInputChange}
            />
          </section>
        );
      case PaymentMethods.CHECK:
        return (
          <section className="test border-b border-foggyGray px-4 pb-10 pt-12  lg:px-26">
            <div className="lg:w-3/4">
              <InputField
                classname="mt-8 w-full p-3"
                label="batchNumber"
                items={mapLocaleToMeaningfulFormat().batchNumberPlaceholder}
                type={'text'}
                htmlFor={'batchNumber'}
                register={register}
                errors={errors}
                id={'batchNumber'}
                maxlength={20}
              />
              <InputField
                classname="mt-8 w-full p-3 "
                label="checkNumber"
                items={mapLocaleToMeaningfulFormat().checkNumberPlaceholder}
                type={'text'}
                htmlFor={'checkNumber'}
                register={register}
                errors={errors}
                id={'checkNumber'}
                maxlength={20}
              />
              <InputField
                classname="mt-8 w-full p-3 "
                label="totalCheckAmount"
                items={mapLocaleToMeaningfulFormat().totalCheckAmountPlaceholder}
                type={'text'}
                register={register}
                htmlFor={'totalCheckAmount'}
                errors={errors}
                id={'totalCheckAmount'}
                onChange={handleInputChange}
              />
            </div>
          </section>
        );
      default:
        return <div></div>;
    }
  };

  const updateBillingData = async (
    currentShippingAddress: Address,
    currentBillingAddress: Address,
    formData: any,
    paymentDetails?: any,
    tokenizeCardData?: TokenizedCardResponse,
  ) => {
    const paymentFormData = formData;
    delete paymentFormData?.newPassword;
    setFormValues(paymentFormData);
    let transactionDetails = paymentDetails;

    if (selectedPaymentMethod === PaymentMethods.CARD) {
      const ctCreditCardData = tokenizeCardData?.cardDetails;
      transactionDetails = {
        avsResponseCode: ctCreditCardData?.avsResponseCode,
        cvvResponseCode: ctCreditCardData?.cvvResponseCode,
        creditCardBin: ctCreditCardData?.creditCardBin,
        creditCardToken: ctCreditCardData?.creditCardToken,
        creditCardType: ctCreditCardData?.creditCardType,
        ccLast4Digits: ctCreditCardData?.ccLast4Digits,
        expirationMonth: ctCreditCardData?.expirationMonth,
        expirationYear: ctCreditCardData?.expirationYear,
        validityChecks: tokenizeCardData?.TokenizeCardResults?.ValidityChecks?.ValidityCheck,
      };
    } else if (selectedPaymentMethod === PaymentMethods.CREDIT) {
      transactionDetails = {
        paymentMethod: 'CREDIT',
        soaCustomer: soaCustomer,
        dob: soaCustomer?.Customer?.CustomerInfo?.dob
          ? soaCustomer?.Customer?.CustomerInfo?.dob
          : formData?.dpqDOB ?? '',
        ssn: soaCustomer?.Customer?.CustomerInfo?.ssn ? soaCustomer?.Customer?.CustomerInfo?.ssn : formData?.ssn ?? '',
      };
    } else if (selectedPaymentMethod === PaymentMethods.CASH || selectedPaymentMethod === PaymentMethods.CHECK) {
      transactionDetails = {
        paymentMethod: selectedPaymentMethod,
        batchNumber: formData?.batchNumber,
        checkNumber: formData?.checkNumber,
        checkAmount: formData?.totalCheckAmount,
        cashAmount: formData?.cashAmount,
      };
    }
    await updateCart({
      shipping: currentShippingAddress,
      billing: { ...currentBillingAddress, country: 'US' },
      account: {
        email: contactInformation?.email ?? '',
      },
      transactionDetails: transactionDetails,
      shouldUpdateShippingAmount: false,
      paymentType: selectedPaymentMethod.toLowerCase(),
    })
      .then(async (billingResponse) => {
        if (billingResponse) {
          setContextCart(billingResponse);
        }
        setBillingAddressValue(billingResponse?.billingAddress);
        goToCheckoutStep(3);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setError(true);
        setNotificationMsg('Error in updating cart');
      });
  };

  const getCardTypeForSOA = (cardType: string) => {
    const lowercaseCardType = cardType?.trim()?.toLowerCase();
    return lowercaseCardType && soaCardTypes[lowercaseCardType] ? soaCardTypes[lowercaseCardType] : '';
  };

  const initiatePaymentFlow = async (
    currentShippingAddress: Address,
    currentBillingAddress: Address,
    formData: any,
  ) => {
    setFormValues(formData);
    if (selectedPaymentMethod === PaymentMethods.CARD) {
      if (creditCardType === 'newCreditCard') {
        const { expMonth, expYear } = PaymentHelpers.getExpirationMonthAndYear(formData?.expDate);
        let cardType = 'unknown';
        getCardType(formData?.cardNumber, (type: any) => (cardType = type));
        try {
          const response: any = await tokenizeCard({
            query: {
              divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
            },
            data: {
              medium: isMailOrder ? 'Mail' : 'Phone',
              CustomerInfo: {
                email: formData?.email,
                phone: currentBillingAddress?.phone ?? '',
                customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? '',
                webSessionId: '',
                ipAddress: '',
              },
              BillingAddress: {
                line1: currentBillingAddress?.streetName ?? '',
                city: currentBillingAddress?.city ?? '',
                state: currentBillingAddress?.state ?? '',
                zip: currentBillingAddress?.postalCode ?? '',
                country: currentBillingAddress?.country ?? '',
              },
              CardInfo: {
                nameOnCard: `${currentBillingAddress?.firstName} ${currentBillingAddress?.lastName}`,
                cardIssuerName: getCardTypeForSOA(cardType),
                pan: formData?.cardNumber.replace(/\s+/g, ''),
                expirationMonth: expMonth,
                expirationYear: expYear,
                securityCode: formData?.securityCode,
              },
            },
          });
          const tokenizeResponse: TokenizeCardResults = response?.TokenizeCardResults;
          if (
            tokenizeResponse?.ValidityChecks?.ValidityCheck?.includes(ORDER_ACCEPTABLE) &&
            tokenizeResponse?.panTokenized?.length > 0
          ) {
            setTokenizeData(tokenizeResponse);
            const creditCardNumber = formData?.cardNumber.replace(/\s+/g, '');
            const currentCreditCard: any = {
              nameOnCard: `${currentBillingAddress?.firstName} ${currentBillingAddress?.lastName}`,
              creditCardType: getCardTypeForSOA(cardType),
              creditCardBin: creditCardNumber.slice(-4),
              expirationMonth: expMonth,
              expirationYear: expYear,
              cvvResponseCode: formData?.securityCode,
              address: currentBillingAddress,
            };
            setCardDetails(currentCreditCard);
            setEncryptedCCDetails(currentCreditCard);
            await updateBillingData(
              currentShippingAddress,
              currentBillingAddress,
              formData,
              currentCreditCard,
              response,
            );
            await startRecording();
          } else {
            setIsLoading(false);
            setError(true);
            setNotificationMsg(mapLocaleToMeaningfulFormat().cardDeclinedError);
          }
        } catch (error) {
          setIsLoading(false);
          setError(true);
          setNotificationMsg(mapLocaleToMeaningfulFormat().tokenizeCardError);
        }
      }
    }
    if (selectedPaymentMethod === PaymentMethods.CREDIT) {
      try {
        await updateBillingData(currentShippingAddress, currentBillingAddress, formData);
        goToCheckoutStep(3);
        await startRecording();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setError(true);
        setNotificationMsg(mapLocaleToMeaningfulFormat().submitPrequalError);
      }
    }

    if (selectedPaymentMethod === PaymentMethods.CASH) {
      const cashAmount = parseFloat(formData?.cashAmount ?? '0.0');
      const cartAmount = (cartData?.total?.centAmount ?? 0) / 100;
      setIsLoading(false);
      if (cashAmount != cartAmount && !proceedFurther) {
        setMismatchModalOpen(true);
        return;
      }
      setIsLoading(true);
      const response: any = await validatePaymentBatch({
        query: {
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
        },
        data: {
          batchId: formData?.batchNumber,
        },
      });
      if (response && response?.code === RESPONSE_SUCCESS_MESSAGE) {
        await updateBillingData(currentShippingAddress, currentBillingAddress, formData);
        setValidatePaymentBatchResult(response?.ValidatePaymentBatchResults);
        setIsLoading(false);
        goToCheckoutStep(3);
        await startRecording();
      } else {
        ErrorNotification(toast, {
          description: 'Invalid Payment Batch',
          action: <ErrorIcon className="h-5 w-5" />,
        });
        setIsLoading(false);
        return;
      }
    }
    if (selectedPaymentMethod === PaymentMethods.CHECK) {
      const cashAmount = parseFloat(formData?.totalCheckAmount ?? '0.0');
      const cartAmount = (cartData?.total?.centAmount ?? 0) / 100;
      setIsLoading(false);
      if (cashAmount != cartAmount && !proceedFurther) {
        setMismatchModalOpen(true);
        return;
      }
      setIsLoading(true);
      const response: any = await validatePaymentBatch({
        query: {
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? '',
        },
        data: {
          batchId: formData?.batchNumber,
        },
      });
      if (response && response?.code === RESPONSE_SUCCESS_MESSAGE) {
        await updateBillingData(currentShippingAddress, currentBillingAddress, formData);
        setValidatePaymentBatchResult(response?.ValidatePaymentBatchResults);
        setIsLoading(false);
        goToCheckoutStep(3);

        await startRecording();
      } else {
        ErrorNotification(toast, {
          description: 'Invalid Payment Batch',
          action: <ErrorIcon className="h-5 w-5" />,
        });
        setIsLoading(false);
        return;
      }
    }
    if (selectedPaymentMethod === PaymentMethods.NO_CHARGE) {
      setIsLoading(true);
      await updateBillingData(currentShippingAddress, currentBillingAddress, null);
      setIsLoading(false);
      goToCheckoutStep(3);
      await startRecording();
    }
  };

  const verifyAddressRef = useRef<(data: any) => Promise<void> | null>(() => Promise.resolve());

  const callVerifyAddress = (data: Address) => {
    if (newAddress && verifyAddressRef?.current) {
      verifyAddressRef.current(getValues());
    } else {
      onSubmit(data);
    }
  };

  const onSubmit = async (data: any) => {
    if (!selectedPaymentMethod) {
      ErrorNotification(toast, {
        description: 'Please select Payment method',
        action: <ErrorIcon className="h-5 w-5" />,
      });
      return;
    }
    setIsLoading(true);

    switch (selectedPaymentMethod) {
      case PaymentMethods.CARD:
        if (creditCardType === 'newCreditCard') {
          setCardDetails(data);
        }
        break;
      case PaymentMethods.CASH:
        setCashDetails(data);
        break;
      case PaymentMethods.CHECK:
        setCheckDetails(data);
        break;
      case PaymentMethods.CREDIT:
        setCreditDetails(data);
        break;
      default:
        // Handle any other cases or provide a default action if needed
        break;
    }

    const currentShippingAddress: Address = {
      firstName: shippingAddress?.firstName,
      lastName: shippingAddress?.lastName,
      phone: shippingAddress?.phone,
      postalCode: shippingAddress?.postalCode,
      state: shippingAddress?.state,
      country: DEFAULT_COUNTRY,
      streetName: shippingAddress?.streetName,
      additionalAddressInfo: shippingAddress?.additionalAddressInfo || '',
      city: shippingAddress?.city,
      externalId: 'Information not needed',
    };

    if (shippingAddress?.addressId) {
      currentShippingAddress.addressId = shippingAddress?.addressId;
    }

    const currentBillingAddress: Address = selectedBillingAddress ? selectedBillingAddress : currentShippingAddress;
    if (billingAddress?.addressId) {
      currentBillingAddress.addressId = billingAddress.addressId;
    }
    try {
      await initiatePaymentFlow(currentShippingAddress, currentBillingAddress, data);
    } catch (error) {
      setIsLoading(false);
      setError(true);
      setNotificationMsg('Error in saving billing address');
    }
  };

  const startRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    setIsLoading(true);
    await startPhoneRecording(payload).catch((err) => err);
    setIsLoading(false);
  };

  const stopRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    setIsLoading(true);
    await stopPhoneRecording(payload).catch((err) => err);
    setIsLoading(false);
  };

  useEffect(() => {
    if (proceedFurther) {
      callVerifyAddress(getValues());
    }
  }, [proceedFurther]);

  const handleChange = (id: string) => {
    const selectedBillingAddress = customerAddresses?.filter((address: any) => address?.addressId === id);
    if (selectedBillingAddress?.length > 0) {
      setSelectedBillingAddress(selectedBillingAddress[0]);
    } else {
      setSelectedBillingAddress(billingAddress);
    }
  };

  const [isCartHold, setIscartHold] = useState(false);
  useEffect(() => {
    if (cartData?.cartState === CART_STATE_FROZEN) {
      setIscartHold(true);
    } else {
      setIscartHold(false);
    }
  }, [cartData]);

  const getBillingAddressComponent = () => {
    return (
      <section className="grid gap-x-2 p-6 lg:grid-cols-2">
        {!isNoChargeOrder && (
          <section className="">
            <strong className="pt-7 text-lg font-bold leading-6">{mapLocaleToMeaningfulFormat().billingAddress}</strong>
            {!(selectedPaymentMethod == PaymentMethods.CREDIT) && (
              <CustomFloatingSelect
                label={'billingAddress'}
                onChange={handleChange}
                id={'billingAddress'}
                register={register}
                setValue={setValue}
                errors={errors}
                classname={'w-full  my-3'}
                placeholder={'*Select Address'}
                options={customerAddresses?.map((items: any) => (
                  <option key={items?.addressId} value={items?.addressId} className="px-2">
                    <p className="gbmask">{items?.fullName}</p>
                  </option>
                ))}
              />
            )}
            <section className="overflow-hidden text-ellipsis pr-24 pt-2.5 text-base font-normal lg:pr-3">
              <section className="gbmask">
                <p>
                  {selectedBillingAddress?.firstName} {selectedBillingAddress?.lastName}
                </p>
                <p>
                  {selectedBillingAddress?.streetNumber} {selectedBillingAddress?.streetName}
                </p>
                <p>{selectedBillingAddress?.additionalAddressInfo}</p>
                <p>
                  {selectedBillingAddress?.city}, {selectedBillingAddress?.state}{' '}
                  {maskPostalCode(selectedBillingAddress?.postalCode)}
                </p>
                {selectedBillingAddress?.phone && (
                  <p className="mt-3">{maskPhoneNumber(selectedBillingAddress?.phone)}</p>
                )}
              </section>
              {!(selectedPaymentMethod == PaymentMethods.CREDIT) && (
                <button
                  type="button"
                  className="pt-2 text-blueLight underline"
                  onClick={() => setOpenEditAddressModal(true)}
                >
                  {mapLocaleToMeaningfulFormat().editAddress}
                </button>
              )}
            </section>

            {openEditAddressModal && (
              <EditAddressModal
                Texts={Texts}
                data={selectedBillingAddress}
                setSelectedBillingAddress={setSelectedBillingAddress}
                open={openEditAddressModal}
                setOpen={setOpenEditAddressModal}
                makeAPIcall={false}
              />
            )}
          </section>
        )}
        {isNoChargeOrder && (
          <article className="">
            <strong className="pt-8 text-lg font-bold leading-6 lg:pt-7">
              {mapLocaleToMeaningfulFormat().accountNumber}
            </strong>
            <p className="gbmask overflow-hidden text-ellipsis pb-9 pr-6 pt-2.5 text-base font-normal lg:pb-0 lg:pr-3">
              {customerId}
            </p>
          </article>
        )}
        {!isNoChargeOrder && (
          <article className="pl-11">
            <strong className="pt-8 text-lg font-bold leading-6 lg:pt-7">{Texts?.contactinfo}</strong>
            <p className="gbmask overflow-hidden text-ellipsis pb-9 pr-6 pt-2.5 text-base font-normal lg:pb-0 lg:pr-3">
              {contactInformation?.email}
            </p>
          </article>
        )}
      </section>
    );
  };

  const creditAccountName = () => {
    switch (divisionCode) {
      case 'ZD':
        return 'Mason Easy-Pay';
      case 'ZB':
        return 'Stoneberry';
      case 'ZY':
        return 'Masseys';
    }
  };

  const fetchDebtBuyersDataStorage = (debtBuyerCode: string) => {
    let debtBuyersDataFromStorage;
    const debtBuyersDataLocalStorage = localStorage.getItem('debtBuyerData');
    if (debtBuyersDataLocalStorage) {
      try {
        const debtBuyersData = JSON.parse(debtBuyersDataLocalStorage);
        if (debtBuyersData) {
          const { data, isSucess, date } = debtBuyersData;
          if (isSucess && date && data) {
            const dateObj = moment(date);
            const currentDate = new Date();
            const currentDateObj = moment(currentDate?.getTime());
            const hoursDiff = currentDateObj.diff(dateObj, 'hours');
            if (data[debtBuyerCode] && hoursDiff <= 6) {
              debtBuyersDataFromStorage = data[debtBuyerCode];
            }
          }
        }
      } catch (error) {
        console.error('Error while parsing debt buyers data');
      }
    }
    return debtBuyersDataFromStorage;
  };

  const fetchDebtBuyerData = async (debtBuyerCode: string) => {
    try {
      const debtBuyersDataFromStorage = fetchDebtBuyersDataStorage(debtBuyerCode);
      if (debtBuyersDataFromStorage) {
        setSoldAccountData(debtBuyersDataFromStorage);
      } else {
        const debtBuyers = await getDebtBuyerData();
        if (debtBuyers && Object.entries(debtBuyers)?.length > 0) {
          if (debtBuyers[debtBuyerCode]) {
            setSoldAccountData(debtBuyers[debtBuyerCode]);
            const debtBuyersData = {
              data: debtBuyers,
              isSucess: true,
              date: new Date(),
            };
            localStorage.setItem('debtBuyerData', JSON.stringify(debtBuyersData));
          }
        }
      }
    } catch (error) {
      console.error('Error while fetching debt buyers data.');
    }
  };

  useEffect(() => {
    if (showSoldAccountSection(projectSettings, isSoldAccount) && debtBuyerCode) {
      fetchDebtBuyerData(debtBuyerCode);
    }
  }, [debtBuyerCode]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="border-y pb-6 pt-22 lg:mt-6 lg:rounded-md lg:border lg:border-foggyGray lg:pt-6 lg:shadow-md lg:shadow-foggyGray">
        <header className="flex items-center gap-x-4 border-foggyGray px-4 align-middle lg:border-b  lg:px-26 lg:pb-5 ">
          <StepsDesktop />
          <h1 className="text-[22px] font-bold leading-7">{Texts?.payment}</h1>
        </header>
        <section className="px-4 pt-7 lg:px-26 lg:pt-6 ">
          <PaymentTitle
            data={tasticData}
            soldAccountData={soldAccountData}
            creditAccountName={creditAccountName}
            accountSoldOn={accountSoldOn}
            isSoldAccount={isSoldAccount}
          />
        </section>
        {getPaymentComponent()}
        {getBillingAddressComponent()}

        {mismatchModalOpen && (
          <OrderTotalMismatchModal
            open={mismatchModalOpen}
            setOpen={setMismatchModalOpen}
            setProceedFurther={setProceedFurther}
          />
        )}
      </section>
      <section className="grid grid-cols-1 py-6 pl-3.5 pr-4 lg:grid-cols-2 lg:px-0">
        <Button
          disabled={isLoading || !selectedPaymentMethod || isCartHold}
          variant="destructive"
          onClick={handleSubmit(callVerifyAddress)}
          type="submit"
          className={`${
            !isLoading ? 'bg-deepBlue text-white' : 'bg-gray-400'
          } py-4 text-[22px] font-bold leading-7  disabled:bg-gray-400 lg:py-3 lg:text-lg lg:leading-6`}
        >
          {isLoading && <Loader />} {Texts?.continueToReview}
        </Button>
      </section>
    </form>
  );
};

export default Payment;
